import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import axiosInstance from "../../utils/axiosInstance";
import MessagingApiConfigForm from "./MessagingApiConfigForm"; // Form for creating/editing configs
import { format } from "date-fns";
import { DATE_FORMAT_AM_PM } from "../../utils/constants";

const MessagingApiConfigDataTable = ({ apiUrl }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0); // Total rows count
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 10, // Default rows per page
  });

  const [formOpen, setFormOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [sortModel, setSortModel] = useState([
    { field: "modifiedDate", sort: "desc" },
  ]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(apiUrl, {
        params: {
          page: paginationModel.page + 1, // Convert 0-based to 1-based indexing for backend
          limit: paginationModel.pageSize,
          search: searchText,
          sortField: sortModel[0]?.field || "modifiedDate",
          sortOrder: sortModel[0]?.sort || "desc",
        },
      });

      const configs = response.data.configs.map((config) => ({
        ...config,
        status: config.status || "Inactive",
        createdDate: config.createdDate || "1970-01-01T00:00:00.000Z",
        modifiedDate: config.modifiedDate || "1970-01-01T00:00:00.000Z",
      }));

      setRows(configs);
      setTotalRows(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching configs:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch configurations.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, paginationModel, searchText, sortModel]);

  const handleSortModelChange = (model) => setSortModel(model);

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleOpenDeleteDialog = (config) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (configToDelete) {
        await axiosInstance.delete(`${apiUrl}/${configToDelete.configId}`);
        setSnackbar({
          open: true,
          message: "Configuration deleted successfully!",
          severity: "success",
        });
        fetchConfigs();
      }
    } catch (error) {
      console.error("Error deleting configuration:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete configuration.",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    }
  };

  const handleOpenAddForm = () => {
    setSelectedConfig(null);
    setFormOpen(true);
  };

  const handleOpenEditForm = (config) => {
    setSelectedConfig(config);
    setFormOpen(true);
  };

  const handleSearch = (event) => setSearchText(event.target.value);

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const columns = [
    { field: "configId", headerName: "Config ID", flex: 0.5 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "apiProvider", headerName: "Provider", flex: 1 },
    { field: "endpoint", headerName: "Endpoint", flex: 1.5 },
    { field: "userName", headerName: "Last Modified By", flex: 1 },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 200,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "modifiedDate",
      headerName: "Modified Date",
      width: 200,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const statusColors = {
          Active: "success",
          Inactive: "default",
        };

        return (
          <Chip
            label={params.row.status || "Unknown"} // Fallback to 'Unknown' if status is null or undefined
            color={statusColors[params.row.status] || "default"} // Use 'default' if the status is not in the mapping
            size="small"
            sx={{
              fontWeight: "bold",
              textTransform: "capitalize", // Make the label look cleaner
              borderRadius: 1, // Less rounded corners
              padding: "0 8px", // Adjust padding for better shape
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="more"
          onClick={(event) => handleMenuOpen(event, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h4">Messaging API Configurations</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearch}
            placeholder="Search by name"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddForm}
          >
            Add Config
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: '70vh',
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
            width: '100%'
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            pagination
            getRowId={(row) => row.configId}
            pageSizeOptions={[10, 25, 50, 100]}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) => {
              console.log("Pagination model changed:", newModel); // Debugging
              setPaginationModel(newModel); // Update pagination state
            }}
            paginationMode="server"
            rowCount={totalRows}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f4f6f8",
                fontWeight: "bold",
              },
            }}
          />
        </Box>
      )}

      <Menu
        id="actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleOpenEditForm(menuRow);
            handleMenuClose();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenDeleteDialog(menuRow);
            handleMenuClose();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <MessagingApiConfigForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initialValues={selectedConfig || {}}
        isUpdate={!!selectedConfig}
        refreshConfigs={fetchConfigs}
        setSnackbar={setSnackbar}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the configuration{" "}
          <strong>{configToDelete?.name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessagingApiConfigDataTable;
